import { ArrowLeft, ChevronLeft, Flag, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { href } from '../app/router';
import { RoleChip } from '../components/Cast';
import { FlagSheet } from '../components/FlagSheet';
import { Illustration } from '../components/Illustration';
import { GreekText } from '../components/GreekText';
import { PlayButton } from '../components/PlayButton';
import { Status } from '../components/Status';
import { allItems, course, frameOf, staffOf } from '../content';
import { whenLabel } from '../lib/day';
import { phraseStatus } from '../lib/progress';
import { cardId, dueDate } from '../lib/scheduler';
import { clipFor, itemVoice, speechRange } from '../lib/voice';
import { WhyNote } from '../session/WhyNote';
import { unflag, useAppState } from '../state/store';

const FLAG_LABEL = { greek: 'the Greek', meaning: 'the meaning', audio: 'the audio', note: 'the note', other: 'something else' };

/** Everything about one phrase: its audio (also slowly), word by word, why and progress. */
export function PhraseDetail({ id }: { id: string }) {
  const state = useAppState();
  const [flagOpen, setFlagOpen] = useState(false);
  const item = allItems.find((i) => i.id === id);
  if (!item) {
    return (
      <main className="page" id="main">
        <a href={href('/phrases')} className="btn btn-ghost" style={{ alignSelf: 'flex-start' }}>
          <ArrowLeft size={18} aria-hidden="true" /> Phrasebook
        </a>
        <h1 className="title">Phrase not found</h1>
        <p>That phrase isn't in the course.</p>
      </main>
    );
  }
  // In the voice of whoever says it in its lesson, like everywhere else.
  const voice = itemVoice(item, staffOf(item));
  const flagged = state.profile.flags[item.id];
  const now = new Date();
  const cards = (item.role === 'say' ? (['say', 'hear'] as const) : (['hear'] as const))
    .map((k) => ({ kind: k, card: state.cards[cardId(item.id, k)] }))
    .filter((c) => c.card);

  return (
    <main className="page" id="main">
      <nav className="navbar" aria-label="Back">
        <a href={href('/phrases')} className="nav-back">
          <ChevronLeft size={24} aria-hidden="true" /> Phrasebook
        </a>
      </nav>

      <header className="stack-lg">
        <div className="row-wrap">
          <RoleChip staff={staffOf(item)} from={item.role === 'say' ? 'you' : 'staff'} />
          <Status status={phraseStatus(state, item.id, item.role)} />
        </div>
        <h1 className="sr-only">{item.meaning}</h1>
        <div className="stack" style={{ gap: 6 }}>
          <GreekText text={item.text} slot={item.slot?.slot} size="hero" ipa={item.ipa} showRespelling />
          <p className="meaning">{item.meaning}</p>
        </div>
        <div className="transport">
          {(() => {
            const clip = clipFor(item, voice);
            const range = speechRange(clip);
            return (
              <>
                <PlayButton src={clip.src} tag={`detail:${id}`} label="Listen" ariaLabel="Listen" from={range.from} to={range.to} />
                <PlayButton src={clip.src} tag={`detail:${id}:slow`} label="Slowly" ariaLabel="Slowly" rate={0.75} from={range.from} to={range.to} />
              </>
            );
          })()}
        </div>
      </header>

      {(item.context || item.spokenText !== item.text) && (
        <div className="detail-scene">
          <Illustration id={item.id} size="cue" decorative className="detail-art" />
          <div className="stack" style={{ gap: 6, minWidth: 0 }}>
            {item.context && <p className="context">{item.context}</p>}
            {item.spokenText !== item.text && (
              <p className="small muted">
                Said as{' '}
                <span className="greek" lang="el" style={{ marginLeft: '0.15em' }}>
                  {item.spokenText}
                </span>
              </p>
            )}
          </div>
        </div>
      )}

      <WhyNote item={item} frame={frameOf(item)} open />

      {item.slot && course.items.filter((m) => m.slot?.frame === item.slot!.frame).length > 1 && (
        <section className="stack" aria-labelledby="swap-h">
          <div className="stack" style={{ gap: 2 }}>
            <h2 className="section-title" id="swap-h">
              Swap the slot
            </h2>
            <p className="small muted">The same pattern with other words in the slot.</p>
          </div>
          <ul className="swap-list">
            {course.items
              .filter((m) => m.slot?.frame === item.slot!.frame)
              .map((m) => (
                <li key={m.id}>
                  <a href={href(`/phrase/${m.id}`)} className="swap" aria-current={m.id === item.id ? 'page' : undefined}>
                    <Illustration id={m.id} size="thumb" decorative className="swap-art" />
                    <span className="greek" lang="el">
                      {m.slot!.slot}
                    </span>
                    <span className="sr-only">: {m.meaning}</span>
                  </a>
                </li>
              ))}
          </ul>
        </section>
      )}

      {cards.length > 0 && (
        <section className="stack" aria-labelledby="progress-h">
          <h2 className="section-title" id="progress-h">
            Your progress
          </h2>
          <div className="panel stack" style={{ gap: 0, padding: '4px 20px' }}>
            {cards.map(({ kind, card }) => (
              <div key={kind} className="setting">
                <div className="setting-text">
                  <span className="setting-label">{kind === 'say' ? 'Saying it' : 'Understanding it'}</span>
                  <span className="setting-help">
                    {card!.fsrs.reps === 0 ? 'Not practised yet' : `Practised ${card!.fsrs.reps} ${card!.fsrs.reps === 1 ? 'time' : 'times'}`}
                    {card!.fsrs.lapses > 0 ? `, forgotten ${card!.fsrs.lapses === 1 ? 'once' : `${card!.fsrs.lapses} times`}` : ''}
                  </span>
                </div>
                <span className="small muted" style={{ textAlign: 'right' }}>
                  Next {whenLabel(dueDate(card!), now)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {flagged ? (
        <div className="stack">
          <p className="small muted">
            You flagged {FLAG_LABEL[flagged.reason]} on {new Date(flagged.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
            {flagged.note ? `: “${flagged.note}”.` : '.'} It's out of your practice.
          </p>
          <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => unflag(item.id)}>
            <Undo2 size={18} aria-hidden="true" /> Withdraw the flag
          </button>
        </div>
      ) : (
        <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => setFlagOpen(true)}>
          <Flag size={18} aria-hidden="true" /> Flag as wrong
        </button>
      )}

      <FlagSheet itemId={item.id} open={flagOpen} onClose={() => setFlagOpen(false)} />
    </main>
  );
}
